/** @jsxImportSource @emotion/react */

import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import * as size from '@constant/size';
import * as color from '@constant/color';
import { ETheme } from '@datatype/type';
import { IStudyPlanAnswer, TStudyPlanStep } from '@datatype/iyagigo/type';
import TextBlock from '@component/common/block/text-block';
import ImageBlock from '@component/common/block/image-block';
import TextButton from '@design-system/text-button';
import DesignOption from '@design-system/option';
import BottomContainer from '@component/common/architecture/bottom-container';
import Scroll, { ScrollContainer as ScrollContentContainer } from '@component/common/scroll';
import Gap from '@component/common/gap';
import otterReadImage from '@asset/image/otter-read.png';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as cacheActions } from '@store/slice/cache-slice';
import {
  IYAGIGO_STUDY_PLAN_LANGUAGE_LEVEL_OPTIONS,
  IYAGIGO_STUDY_PLAN_STEPS,
  IYAGIGO_STUDY_PLAN_STUDY_TIME_GOAL_OPTIONS,
} from '@constant/iyagigo/variable';
import {
  EApiLanguageCode,
  EApiLanguageLevel,
  EApiSourceChannel,
  EApiStudyMotivation,
  EApiStudyTimeGoal,
  ICreateStudentRequest,
} from '@datatype/iyagigo/api-type';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { LOCAL_STORAGE_STUDENT_KEY } from '@config/auth';
import { useCreateStudentMutation, useModifyStudentMutation } from '@hook/iyagigo/useApi';
import { useAuthPage } from '@hook/auth/useAuth';

// ###################################
// StudyPlan — 첫 학습 전 학습 계획 설문 4단계
// - 라우트: /ig/study-plan
// - grammar-study 패턴 미러 (FixContentContainer 고정 + Scroll + BottomContainer, step별 조건 렌더)
// - 질문 문구/옵션은 하단 상수 (TODO: 개발 완료 후 variable.ts로 이동)
// ###################################

const BOTTOM_CONTAINER_HEIGHT = '85px';
const ACCOUNT_CHOICE_BOTTOM_CONTAINER_HEIGHT = '130px';

// ############### 설문 문구 / 옵션 (TODO: 개발 완료 후 variable.ts로 이동) ###############
interface IOption<T> {
  value: T;
  text: string;
  // 보조 설명 (없으면 미노출)
  description?: string;
}

const SOURCE_QUESTION = '이야기고를 어떻게 알게 되셨나요?';

// STORE(스토어 검색 = ASO)와 SEARCH(웹 검색 = SEO)는 개선 액션이 달라 분리.
// 라벨에 '검색'을 공통으로 넣어 같은 축임을 드러내고, description으로 어디서 검색했는지 구분.
// 순서 = 예상 유입량 순. 설문은 위쪽 선택지가 더 많이 선택되므로(순서 편향),
// 실제로 많을 항목을 위에 둬야 사용자가 자기 경우를 빨리 찾고 대충 고르는 일이 줄어든다.
// 상위 3개(SNS/스토어/인터넷검색)가 각각 Meta광고·ASO·SEO에 대응 — 이 비율이 곧 개선 우선순위.
const SOURCE_OPTIONS: IOption<EApiSourceChannel>[] = [
  { value: EApiSourceChannel.SNS, text: 'SNS', description: '인스타그램 · 유튜브에서 봤어요' },
  { value: EApiSourceChannel.STORE, text: '스토어에서 검색', description: '앱을 찾다가 발견했어요' },
  { value: EApiSourceChannel.SEARCH, text: '인터넷 검색', description: '구글이나 네이버에서 검색했어요' },
  { value: EApiSourceChannel.ARTICLE, text: '블로그 · 커뮤니티', description: '블로그나 카페에서 봤어요' },
  { value: EApiSourceChannel.REFERRAL, text: '지인 추천', description: '추천받아서 알게 됐어요' },
  { value: EApiSourceChannel.ETC, text: '기타', description: '다른 경로로 알게 됐어요' },
];

const MOTIVATION_DESCRIPTION = '여러 개 선택해도 좋아요';

// 라벨은 명사형(목적 축)으로 통일. 순서 = 예상 응답률 순.
const JAPANESE_MOTIVATION_OPTIONS: IOption<EApiStudyMotivation>[] = [
  { value: EApiStudyMotivation.HOBBY, text: '취미', description: '드라마 · 애니 · 음악을 즐기고 싶어요' },
  { value: EApiStudyMotivation.CONVERSATION, text: '회화', description: '여행이나 일상에서 말하고 싶어요' },
  { value: EApiStudyMotivation.STUDY, text: '학업', description: '시험이나 유학을 준비하고 있어요' },
  { value: EApiStudyMotivation.WORK, text: '업무', description: '일할 때 써야 해요' },
  { value: EApiStudyMotivation.ETC, text: '기타', description: '새로운 언어를 배우고 싶어요' },
];

const ENGLISH_MOTIVATION_OPTIONS: IOption<EApiStudyMotivation>[] = [
  { value: EApiStudyMotivation.HOBBY, text: '취미', description: '영화 · 드라마 · 음악을 즐기고 싶어요' },
  { value: EApiStudyMotivation.CONVERSATION, text: '회화', description: '여행이나 일상에서 말하고 싶어요' },
  { value: EApiStudyMotivation.STUDY, text: '학업', description: '시험이나 유학을 준비하고 있어요' },
  { value: EApiStudyMotivation.WORK, text: '업무', description: '일할 때 써야 해요' },
  { value: EApiStudyMotivation.ETC, text: '기타', description: '새로운 언어를 배우고 싶어요' },
];

// TODO: student 생성 시 이 응답을 학습 난이도에 반영 (현재는 수집만)
const LEVEL_DESCRIPTION = '딱 맞는 난이도로 시작해요';

const DAILY_MINUTES_QUESTION = '매일 몇 분씩 해볼까요?';
const DAILY_MINUTES_DESCRIPTION = '짧아도 매일이 중요해요';

const INITIAL_STUDY_PLAN_ANSWER: IStudyPlanAnswer = {
  sourceChannel: null,
  studyMotivation: [],
  languageLevel: null,
  studyTimeGoal: null,
};

const createStudyPlanRequest = (
  answer: IStudyPlanAnswer,
  studyLanguage: EApiLanguageCode,
): ICreateStudentRequest | null => {
  const { sourceChannel, languageLevel, studyTimeGoal } = answer;
  if (!sourceChannel || !languageLevel || !studyTimeGoal) return null;

  return {
    studyLanguage,
    sourceChannel,
    studyMotivation: answer.studyMotivation,
    languageLevel,
    studyTimeGoal,
  };
};

// ############### styled (레이아웃 전용) ###############
const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  padding: ${size.PAGE_PADDING} ${size.PAGE_PADDING} 0 ${size.PAGE_PADDING};
  display: flex;
  flex-flow: column nowrap;
`;

// 스크롤 밖 상단 고정 영역 (grammar-study FixContentContainer 패턴) — 오터 + 질문
const FixContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
`;

const BeginContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const QuestionContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-start;
  gap: ${size.MARGIN_1};
`;

const QuestionTextContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  height: 100%;
`;

const BottomContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: 100%;
  //gap: ${size.MARGIN_1};
  padding: 0 ${size.PAGE_PADDING} ${IS_IYAGIGO_MOBILE_VIEW ? '0px' : size.MARGIN_4} ${size.PAGE_PADDING};
`;

// Option이 hover 시 translateY(-1px)로 뜨는데, Scroll(overflow hidden) 최상단에 붙어 있으면
// 첫 카드의 위쪽 테두리가 잘린다. 상단 여백으로 회피.
const OptionsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 10px;
  width: 100%;
  padding-top: 2px;
`;

interface IProps {
  existingSourceChannel?: EApiSourceChannel | null;
  existingStudyLanguage?: EApiLanguageCode | null;
}

const StudyPlan: React.FC<IProps> = ({ existingSourceChannel = null, existingStudyLanguage = null }) => {
  const navigate = useNavigate();
  const dispatch = useTypedDispatch();
  const { openAuthPage } = useAuthPage();
  const currentStudyLanguage =
    useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? EApiLanguageCode.JAPANESE;
  const step = useTypedSelector((state) => state.rootReducer?.cacheReducer?.studyPlanStep) ?? 'account-choice';
  const [answer, setAnswer] = useState<IStudyPlanAnswer>(INITIAL_STUDY_PLAN_ANSWER);
  const createStudentMutation = useCreateStudentMutation();
  const modifyStudentMutation = useModifyStudentMutation();

  const studyPlanSteps = existingSourceChannel
    ? IYAGIGO_STUDY_PLAN_STEPS.filter((item) => item !== 'source-channel')
    : IYAGIGO_STUDY_PLAN_STEPS;
  const stepIndex = studyPlanSteps.indexOf(step);
  const isLastStep = stepIndex >= studyPlanSteps.length - 1;
  const studyLanguage = existingStudyLanguage ?? currentStudyLanguage;
  const isEnglish = studyLanguage === EApiLanguageCode.ENGLISH;
  const motivationOptions = isEnglish ? ENGLISH_MOTIVATION_OPTIONS : JAPANESE_MOTIVATION_OPTIONS;
  const sourceChannel = existingSourceChannel ?? answer.sourceChannel;

  const handleSubmitClick = useCallback(
    async (submitted: IStudyPlanAnswer) => {
      const request = createStudyPlanRequest({ ...submitted, sourceChannel }, studyLanguage);
      if (!request) return;

      const studentKey = localStorage.getItem(LOCAL_STORAGE_STUDENT_KEY) ?? undefined;
      const response = studentKey
        ? await modifyStudentMutation.mutateAsync({ studentKey, ...request })
        : await createStudentMutation.mutateAsync(request);
      localStorage.setItem(LOCAL_STORAGE_STUDENT_KEY, response.data.key);
    },
    [createStudentMutation, modifyStudentMutation, sourceChannel, studyLanguage],
  );

  const handleMemberStartClick = () => {
    openAuthPage('signin');
  };

  const handleGuestStartClick = () => {
    dispatch(cacheActions.storeStudyPlanStep(IYAGIGO_STUDY_PLAN_STEPS[1]));
  };

  const handleNextClick = async () => {
    switch (step) {
      case 'source-channel':
        if (answer.sourceChannel === null) return;
        break;
      case 'study-motivation':
        if (answer.studyMotivation.length === 0) return;
        break;
      case 'language-level':
        if (answer.languageLevel === null) return;
        break;
      case 'study-time-goal':
        if (answer.studyTimeGoal === null) return;
        break;
    }

    if (isLastStep) {
      await handleSubmitClick(answer);
      navigate(IYAGIGO_PATH.INITIAL_SERVICE_PATH, { replace: true });
      return;
    }

    dispatch(cacheActions.storeStudyPlanStep(studyPlanSteps[stepIndex + 1]));
  };

  const handleSourceOptionClick = (sourceChannel: EApiSourceChannel) => {
    setAnswer((prev) => ({ ...prev, sourceChannel }));
  };

  const handleMotivationOptionClick = (studyMotivation: EApiStudyMotivation) => {
    setAnswer((prev) => ({
      ...prev,
      studyMotivation: prev.studyMotivation.includes(studyMotivation)
        ? prev.studyMotivation.filter((item) => item !== studyMotivation)
        : [...prev.studyMotivation, studyMotivation],
    }));
  };

  const handleLevelOptionClick = (languageLevel: EApiLanguageLevel) => {
    setAnswer((prev) => ({ ...prev, languageLevel }));
  };

  const handleStudyTimeGoalOptionClick = (studyTimeGoal: EApiStudyTimeGoal) => {
    setAnswer((prev) => ({ ...prev, studyTimeGoal }));
  };

  const question = (() => {
    switch (step) {
      case 'account-choice':
      case 'introduction':
        return { text: '', description: undefined };
      case 'source-channel':
        return { text: SOURCE_QUESTION, description: undefined };
      case 'study-motivation':
        return { text: `왜 ${isEnglish ? '영어' : '일본어'}를 배우시나요?`, description: MOTIVATION_DESCRIPTION };
      case 'language-level':
        return { text: `현재 ${isEnglish ? '영어' : '일본어'} 수준은?`, description: LEVEL_DESCRIPTION };
      case 'study-time-goal':
        return { text: DAILY_MINUTES_QUESTION, description: DAILY_MINUTES_DESCRIPTION };
    }
  })();

  const SourceOptions = SOURCE_OPTIONS.map((option) => (
    <DesignOption
      key={option.value}
      theme={ETheme.BROWN}
      align='left'
      width='100%'
      label={option.text}
      value={option.description}
      selected={sourceChannel === option.value}
      handleClick={() => handleSourceOptionClick(option.value)}
    />
  ));

  const MotivationOptions = motivationOptions.map((option) => (
    <DesignOption
      key={option.value}
      theme={ETheme.BROWN}
      align='left'
      width='100%'
      label={option.text}
      value={option.description}
      selected={answer.studyMotivation.includes(option.value)}
      handleClick={() => handleMotivationOptionClick(option.value)}
    />
  ));

  const LevelOptions = IYAGIGO_STUDY_PLAN_LANGUAGE_LEVEL_OPTIONS.map((option) => (
    <DesignOption
      key={option.value}
      theme={ETheme.BROWN}
      align='left'
      width='100%'
      label={option.text}
      value={option.description}
      selected={answer.languageLevel === option.value}
      handleClick={() => handleLevelOptionClick(option.value)}
    />
  ));

  const StudyTimeGoalOptions = IYAGIGO_STUDY_PLAN_STUDY_TIME_GOAL_OPTIONS.map((option) => (
    <DesignOption
      key={option.value}
      theme={ETheme.BROWN}
      align='left'
      width='100%'
      label={option.text}
      value={option.description}
      selected={answer.studyTimeGoal === option.value}
      handleClick={() => handleStudyTimeGoalOptionClick(option.value)}
    />
  ));

  return (
    <Wrapper>
      {step !== 'account-choice' && step !== 'introduction' && (
        <FixContentContainer>
          <QuestionContainer>
            <ImageBlock src={otterReadImage} alt='이야기고' design={{ image: { height: '70px' } }} />
            <QuestionTextContainer>
              <TextBlock
                text={question.text}
                design={{
                  text: { size: 'large', color: color.BLACK_2, bold: true },
                  box: { alignX: 'left' },
                }}
              />
              {question.description && (
                <TextBlock
                  text={question.description}
                  design={{
                    text: { size: 'medium', color: color.GRAY_1 },
                    box: { alignX: 'left' },
                  }}
                />
              )}
            </QuestionTextContainer>
          </QuestionContainer>
        </FixContentContainer>
      )}
      {step !== 'account-choice' && step !== 'introduction' && <Gap height={size.MARGIN_2} />}
      <ScrollContentContainer>
        <Scroll key={step} direction='y' height='100%'>
          {step === 'account-choice' && (
            <BeginContentContainer>
              <ImageBlock src={otterReadImage} alt='이야기고' design={{ image: { height: '100px' } }} />
              <Gap height={size.MARGIN_2} />
              <TextBlock
                text='어떻게 시작할까요?'
                design={{
                  text: { size: 'large', color: color.BLACK_2, bold: true },
                  box: { alignX: 'center' },
                }}
              />
              {/*<Gap height={size.MARGIN_1} />*/}
              <TextBlock
                text='계정이 있으면 학습 기록을 이어갈 수 있어요'
                design={{
                  text: { size: 'medium', color: color.GRAY_1 },
                  box: { alignX: 'center' },
                }}
              />
            </BeginContentContainer>
          )}
          {step === 'introduction' && (
            <BeginContentContainer>
              <ImageBlock src={otterReadImage} alt='이야기고' design={{ image: { height: '100px' } }} />
              <Gap height={size.MARGIN_2} />
              <TextBlock
                text='더 나은 학습을 위해'
                design={{
                  text: { size: 'large', color: color.BLACK_2, bold: true },
                  box: { alignX: 'center' },
                }}
              />
              <Gap height={size.MARGIN_1} />
              <TextBlock
                text={`간단한 질문 ${existingSourceChannel ? 3 : 4}개만 답해 주세요`}
                design={{
                  text: { size: 'medium', color: color.GRAY_1 },
                  box: { alignX: 'center' },
                }}
              />
            </BeginContentContainer>
          )}
          {step === 'source-channel' && <OptionsContainer>{SourceOptions}</OptionsContainer>}
          {step === 'study-motivation' && <OptionsContainer>{MotivationOptions}</OptionsContainer>}
          {step === 'language-level' && <OptionsContainer>{LevelOptions}</OptionsContainer>}
          {step === 'study-time-goal' && <OptionsContainer>{StudyTimeGoalOptions}</OptionsContainer>}
        </Scroll>
      </ScrollContentContainer>
      <BottomContainer
        position='relative'
        height={step === 'account-choice' ? ACCOUNT_CHOICE_BOTTOM_CONTAINER_HEIGHT : BOTTOM_CONTAINER_HEIGHT}
        backgroundColor={color.WHITE_1}
        margin={`0 -${size.PAGE_PADDING}`}
        animationKey={step}>
        <BottomContentContainer>
          {step === 'account-choice' ? (
            <>
              <TextButton
                text='회원으로 시작하기'
                theme={ETheme.BROWN}
                shape='pill'
                size='medium'
                width='100%'
                handleClick={handleMemberStartClick}
              />
              <Gap height={size.MARGIN_1} />
              <TextButton
                text='비회원으로 시작하기'
                theme={ETheme.BROWN}
                kind='outlined'
                shape='pill'
                size='medium'
                width='100%'
                handleClick={handleGuestStartClick}
              />
            </>
          ) : (
            <TextButton
              text={step === 'introduction' ? '시작하기' : step === 'study-time-goal' ? '학습 시작하기' : '다음'}
              theme={ETheme.BROWN}
              shape='pill'
              size='medium'
              width='100%'
              disabled={
                (step === 'source-channel' && sourceChannel === null) ||
                (step === 'study-motivation' && answer.studyMotivation.length === 0) ||
                (step === 'language-level' && answer.languageLevel === null) ||
                (step === 'study-time-goal' && answer.studyTimeGoal === null)
              }
              handleClick={handleNextClick}
            />
          )}
        </BottomContentContainer>
      </BottomContainer>
    </Wrapper>
  );
};

export default StudyPlan;
